import { z } from 'zod';
import { toFlatField } from '../oasis-types';
export const baseHearingImpairment = z.object({
    wnl: z.boolean().optional(),
    leftEar: z.string().optional(),
    rightEar: z.string().optional(),
});
export const baseHearingImpairmentRefined = baseHearingImpairment.refine((data) => data.wnl || data.leftEar || data.rightEar, {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const baseNeurological = z.object({
    wnl: z.boolean().optional(),
    alertness: z.string().optional(),
    orientedToPerson: z.string().optional(),
    orientedToPlace: z.string().optional(),
    orientedToPurpose: z.string().optional(),
    orientedToTime: z.string().optional(),
    status: z.array(z.string()).optional(),
    lueParesisWeakness: z.string().optional(),
    rueParesisWeakness: z.string().optional(),
    lleParesisWeakness: z.string().optional(),
    rleParesisWeakness: z.string().optional(),
    handGrip: z.string().optional(),
    handGripReaction: z.string().optional(),
});
export const baseNeurologicalRefined = baseNeurological.refine((data) => data.wnl ||
    data.alertness ||
    data.orientedToPerson ||
    data.orientedToPlace ||
    data.orientedToPurpose ||
    data.orientedToTime ||
    data.status?.length ||
    data.lueParesisWeakness ||
    data.rueParesisWeakness ||
    data.lleParesisWeakness ||
    data.rleParesisWeakness ||
    data.handGrip ||
    data.handGripReaction, {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const basePupilImpairments = z.object({
    wnl: z.boolean().optional(),
    pupilIsEqual: z.string().optional(),
    leftPupil: z.string().optional(),
    rightPupil: z.string().optional(),
    leftImpairment: z.string().optional(),
    rightImpairment: z.string().optional(),
});
export const basePupilImpairmentsRefined = basePupilImpairments.refine((data) => data.wnl || data.pupilIsEqual || data.leftPupil || data.rightPupil || data.leftImpairment || data.rightImpairment, {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const baseMouthNoseThroat = z.object({
    wnl: z.boolean().optional(),
    status: z.array(z.string()).optional(),
});
export const baseMouthNoseThroatRefined = baseMouthNoseThroat.refine((data) => data.wnl || data.status?.length, {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
export const SectionBVersioned = {
    b0200: {
        e1: { alias: 'b0200', timepoint: ['soc'], tags: ['PRA'] },
        e2: { alias: 'b0200', timepoint: ['soc', 'roc'], tags: ['PRA'] },
    },
    b1000: {
        e1: { alias: 'b1000', timepoint: ['soc'], tags: ['PRA'] },
        e2: { alias: 'b1000', timepoint: ['soc', 'roc'], tags: ['PRA'] },
    },
    hearingImpairment: {
        e1: { alias: 'hearingImpairment', timepoint: ['soc'] },
    },
    pupilImpairments: {
        e1: { alias: 'pupilImpairments', timepoint: ['soc'] },
    },
    mouthNoseThroat: {
        e1: { alias: 'mouthNoseThroat', timepoint: ['soc'] },
    },
    neurological: {
        e1: { alias: 'neurological', timepoint: ['soc'] },
    },
    b1300: {
        e1: { alias: 'b1300', timepoint: ['soc', 'roc', 'dsch'], tags: ['PRA'] },
    },
};
export const SectionB = toFlatField(SectionBVersioned);
// B-B0200-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Adequate - no difficulty in normal conversation, social interaction, listening to TV"},{"value":"1","label":"Minimal difficulty - difficulty in some environments (e.g., when person speaks softly or setting is noisy)"},{"value":"2","label":"Moderate difficulty - speaker has to increase volume and speak distinctly"},{"value":"3","label":"Highly impaired - absence of useful hearing"},{"value":"-","label":"Not assessed/no information"}]
// B-B1000-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Adequate - sees fine detail, such as regular print in newspapers/books"},{"value":"1","label":"\\tImpaired - sees large print, but not regular print in newspapers/books"},{"value":"2","label":"Moderate impaired - limited vision; not able to see newspaper headlines but can identify objects"},{"value":"3","label":"Highly impaired - object identification in question, but eyes appear to follow objects"},{"value":"4","label":"Severely impaired - no vision or sees only light, colors or shapes; eyes do not appear to follow objects"},{"value":"-","label":"Not assessed/no information"}]
// B-B1300-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Never"},{"value":"1","label":"Rarely"},{"value":"2","label":"Sometimes"},{"value":"3","label":"Often"},{"value":"4","label":"Always"},{"value":"8","label":"Patient unable to respond"},{"value":"7","label":"Patient declines to respond"}]
